import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ChangePasswordCard } from "../components/ChangePasswordCard";
import { signOut } from "../features/auth/authSlice";
import { PUBLIC_BRAND_NAME } from "../services/apiConfig";
import { logEvent } from "../services/logger";

/**
 * "Choose your own password" - the only screen an account sees until it
 * replaces the temporary password someone else gave it (a school admin, a
 * roster import). Plan step 3.
 *
 * It replaces the whole app rather than sitting inside it: the app shell loads
 * notices, notifications and the dashboard, and the server refuses all of them
 * until the password is changed (core/deps.py). Laid out like the sign-in
 * page, because to the person it is the last step of signing in.
 */
export const FirstPasswordPage = () => {
    const dispatch = useAppDispatch();
    const profile = useAppSelector((state) => state.auth.profile);
    const brand = useAppSelector((state) => state.tenant.branding?.display_name) ?? PUBLIC_BRAND_NAME;

    useEffect(() => {
        logEvent({ level: "INFO", component: "FirstPasswordPage", message: "first_password.shown" });
    }, []);

    return (
        <div className="login-shell">
            <section className="login-hero">
                <span className="login-hero__brand">{brand}</span>
                <div className="login-hero__center">
                    <p className="login-hero__eyebrow">First sign-in</p>
                    <h1 className="first-password__headline">Make this account yours.</h1>
                    <p className="first-password__lead">
                        The password you were given was chosen by someone else. Replace it once, and it is yours from
                        here on.
                    </p>
                </div>
                <p className="login-hero__foot">© {new Date().getFullYear()} {brand}</p>
            </section>
            <section className="login-panel">
                <div className="login-card first-password">
                    <ChangePasswordCard
                        bare
                        title={profile?.fullName ? `Welcome, ${profile.fullName}` : "Welcome"}
                        intro="Choose your own password to continue."
                        currentLabel="Temporary password"
                        submitLabel="Save and continue"
                    />
                    <button type="button" className="btn btn-link first-password__signout" onClick={() => dispatch(signOut())}>
                        Not you? Sign out
                    </button>
                </div>
            </section>
        </div>
    );
};
